import log from 'loglevel';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetPositionById, useUpdatePositionState } from '../../hooks/usePositions';
import { POSITION_DETAILS_TABS } from '../../consts';
import { PositionTabs } from '../../components/PositionDetails/PositionTabs';
import useNavigate from '../../hooks/useNavigate';
import { useAuth } from '../../hooks/useAuth';
import { PERMISSIONS, PERMISSIONS_GROUPS } from '../../consts/permissions';
import { NoPermissions } from '../../components/NoPermissions';
import { VIEW_MODE } from '../../consts/enums';
import { usePositionDetailsPageQuery } from '../../hooks/usePositionDetailsPageQuery';
import { HistoryMainPanel } from '../../components/HistoryMainPanel';
import { useHistoryLogsPosition } from '../../hooks/useHistoryLogsPosition';
import STRING_KEYS from '../../language/keys';
import { PositionState } from '../../@types/position';
import PositionDetailsHeader from './PositionDetailsHeader';
import PositionDetailsContainer from './PositionDetailsContainer';
import { AppMatchesTab } from './AppMatchesTab';
import { PositionDetailsRuns } from './PositionDetailsRuns';
import { PARAMS_KEYS } from './paramKeys';
import { CandidateMatchesTable } from './CandidateMatchesTable';

const viewMode = VIEW_MODE.EDITABLE;
const TAG = '[POSITION_DETAILS_PAGE]';

export default function PositionDetailsPage(): JSX.Element {
  const { positionId = '' } = useParams();
  const { goToPositionDetails } = useNavigate();
  const { hasPermissions } = useAuth();
  const { t: translate } = useTranslation();
  const { position, loadPosition, refetchPosition } = useGetPositionById(positionId || '');

  const {
    onSearchParams,
    searchParams,
  } = usePositionDetailsPageQuery();
  const handleOpenHistoryView = () => {
    onSearchParams({
      [PARAMS_KEYS.SHOW_HISTORY]: 'true',
    });
  };
  const accountId = position?.accountId || '';

  const {
    data,
    // isLoading,
    isError,
    error: historyError,
    isFetching,
  } = useHistoryLogsPosition(accountId, positionId,
    () => {
      log.debug(TAG, 'loaded position history logs');
    },
  );

  const updatePositionState = useUpdatePositionState(refetchPosition);

  const handleClosePosition = async (positionState: PositionState) => {
    updatePositionState.mutate({
      positionId,
      accountId,
      positionState,
    });
  };

  const isHistoryVisible =
    searchParams.get(PARAMS_KEYS.SHOW_HISTORY) === 'true' &&
    hasPermissions([PERMISSIONS.POSITION_HISTORY_LOG_VIEW]);

  const handleCloseHistoryView = () => {
    onSearchParams({ [PARAMS_KEYS.SHOW_HISTORY]: 'false' });
  };

  const handleNavigate = (tab: string) => {
    goToPositionDetails(positionId, tab);
  };

  const getDefaultTab = () => {
    if (hasPermissions(PERMISSIONS_GROUPS.POSITION_MATCHES_TAB)) {
      return POSITION_DETAILS_TABS.APP;
    } if (hasPermissions([PERMISSIONS.MATCH_CANDIDATE_VIEW])) {
      return POSITION_DETAILS_TABS.RESULTS;
    }
    
    return '';
  };

  const getCurrentTab = () => {
    const urlTab = searchParams.get(PARAMS_KEYS.TAB);
    
    if (urlTab === POSITION_DETAILS_TABS.APP && hasPermissions(PERMISSIONS_GROUPS.POSITION_MATCHES_TAB)) {
      return POSITION_DETAILS_TABS.APP;
    }
    if (urlTab === POSITION_DETAILS_TABS.RESULTS && hasPermissions([PERMISSIONS.MATCH_CANDIDATE_VIEW])) {
      return POSITION_DETAILS_TABS.RESULTS;
    }
    if (urlTab === POSITION_DETAILS_TABS.RUNS && hasPermissions([PERMISSIONS.MATCH_CANDIDATE_VIEW])) {
      return POSITION_DETAILS_TABS.RUNS;
    }
    
    return getDefaultTab();
  };

  const currentTab = getCurrentTab();

  const renderPositionContent = () => {
    if (!currentTab) {
      return <NoPermissions />;
    }
    
    if (currentTab === POSITION_DETAILS_TABS.RUNS) {
      return (
        <PositionDetailsRuns
          positionId={ positionId }
          accountId={ accountId }
          viewMode={ viewMode }
        />
      );
    }
    if (currentTab === POSITION_DETAILS_TABS.RESULTS) {
      return (
        <CandidateMatchesTable
          positionId={ positionId }
          accountId={ accountId }
        />
      );
    }
    if (currentTab === POSITION_DETAILS_TABS.APP) {
      return <AppMatchesTab />;
    }

    return <NoPermissions />;
  };

  return (
    <PositionDetailsContainer isLoading={ loadPosition }>
      <PositionDetailsHeader
        position={ position }
        onViewHistoryClick={ handleOpenHistoryView }
        onClosePositionHandler={ handleClosePosition }
      />
      <PositionTabs
        currentTab={ currentTab }
        onNavigateClick={ handleNavigate }
      />
      {renderPositionContent()}
      <HistoryMainPanel
        isError={ isError }
        error={ historyError }
        isOpened={ isHistoryVisible }
        onCloseClick={ handleCloseHistoryView }
        data={ data }
        isFetching={ isFetching }
        entity={ translate(STRING_KEYS.POSITION) }
      />
    </PositionDetailsContainer>
  );
}
